import { useQuery } from '@tanstack/react-query'
import { adminApi } from '@/api/workspaces'
import { RefreshFailed } from '@/components/RefreshFailed'
import { adminCopy } from '@/copy'
import type { SystemStatus } from '@/types/api'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { Card } from '@/ui/Card/Card'
import { EmptyState } from '@/ui/EmptyState/EmptyState'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Grid } from '@/ui/layout/Grid'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import { StatusDot } from '@/ui/Status/Status'
import { Table } from '@/ui/Table/Table'
import { formatAge } from '../../utils/time'

const STALE_HEARTBEAT_MS = 120_000

const dash = <Text tone="text-mute">—</Text>

export function SystemTab() {
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['admin', 'status'],
    queryFn: adminApi.systemStatus,
    refetchInterval: 5000,
  })
  const status = data as SystemStatus | undefined
  const showSkeleton = useSkeletonDelay(isPending)

  // Loading and failure keep the section header, as every other tab does.
  if (isPending || !status) {
    return (
      <section>
        <SectionHeader rule trailingSpacing="section">
          {adminCopy.system.heading}
        </SectionHeader>
        {isPending ? (
          <div aria-busy="true">
            {showSkeleton ? <Skeleton lines={4} /> : null}
          </div>
        ) : (
          <Alert tone="danger">
            <Inline gap="space-8" align="baseline" wrap>
              <Text>{adminCopy.loadFailed.status}</Text>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void refetch()}
              >
                {adminCopy.retry}
              </Button>
            </Inline>
          </Alert>
        )}
      </section>
    )
  }

  return (
    <Stack gap="space-24">
      <section>
        <SectionHeader rule trailingSpacing="section">
          {adminCopy.system.heading}
        </SectionHeader>
        <Stack gap="space-12">
          {isError && (
            <RefreshFailed
              message={adminCopy.refreshFailed.status}
              onRetry={() => void refetch()}
            />
          )}
          <Grid columns={2} mdColumns={4} gap="space-8">
            <Card
              variant="stat"
              label={adminCopy.system.stats.mode}
              value={status.mode}
            />
            <Card
              variant="stat"
              label={adminCopy.system.stats.actors}
              value={status.actors.count}
            />
            <Card
              variant="stat"
              label={adminCopy.system.stats.turns}
              value={status.active_turns.length}
            />
            <Card
              variant="stat"
              label={adminCopy.system.stats.workers}
              value={status.workers.length}
            />
            <Card
              variant="stat"
              label={adminCopy.system.stats.subscribers}
              value={status.subscribers}
            />
            <Card
              variant="stat"
              label={adminCopy.system.stats.eventBuffer}
              value={status.event_buffer_len}
            />
            <Card
              variant="stat"
              label={adminCopy.system.stats.dbPool}
              value={`${status.db_pool.size - status.db_pool.idle}/${status.db_pool.size}`}
            />
            <Card
              variant="stat"
              label={adminCopy.system.stats.topics}
              value={status.subscribed_topics}
            />
          </Grid>
        </Stack>
      </section>

      <section>
        <SectionHeader
          rule
          trailingSpacing="section"
          count={status.actors.count}
        >
          {adminCopy.system.actorsHeading}
        </SectionHeader>
        <Table
          caption={adminCopy.system.actors}
          rows={status.actors.agents}
          getRowId={(a) => a.agent_id}
          empty={
            <EmptyState
              variant="inline"
              description={adminCopy.system.noActors}
            />
          }
          columns={[
            {
              key: 'agent',
              header: adminCopy.system.columns.agent,
              render: (a) => (
                <Text variant="code" tone="text-sub">
                  {a.agent_id}
                </Text>
              ),
            },
            {
              key: 'name',
              header: adminCopy.system.columns.name,
              render: (a) => a.name ?? dash,
            },
            {
              key: 'workspace',
              header: adminCopy.system.columns.workspace,
              render: (a) =>
                a.workspace_id ? (
                  <Text variant="code" tone="text-sub">
                    {a.workspace_id}
                  </Text>
                ) : (
                  dash
                ),
            },
          ]}
        />
      </section>

      {status.active_turns.length > 0 && (
        <section>
          <SectionHeader
            rule
            trailingSpacing="section"
            count={status.active_turns.length}
          >
            {adminCopy.system.turnsHeading}
          </SectionHeader>
          <Table
            caption={adminCopy.system.turns}
            rows={status.active_turns}
            getRowId={(t) => t.run_id}
            columns={[
              {
                key: 'agent',
                header: adminCopy.system.columns.agent,
                render: (t) => (
                  <Text variant="code" tone="text-sub">
                    {t.agent_id}
                  </Text>
                ),
              },
              {
                key: 'topic',
                header: adminCopy.system.columns.topic,
                render: (t) => (
                  <Text variant="code" tone="text-sub">
                    {t.topic_id}
                  </Text>
                ),
              },
              {
                key: 'thread',
                header: adminCopy.system.columns.thread,
                render: (t) =>
                  t.thread_id ? (
                    <Text variant="code" tone="text-sub">
                      {t.thread_id}
                    </Text>
                  ) : (
                    dash
                  ),
              },
              {
                key: 'age',
                header: adminCopy.system.columns.age,
                align: 'end',
                render: (t) => (
                  <Text tone="text-sub" numeric>
                    {formatAge(status.server_time_ms - t.started_at)}
                  </Text>
                ),
              },
            ]}
          />
        </section>
      )}

      {status.workers.length > 0 && (
        <section>
          <SectionHeader
            rule
            trailingSpacing="section"
            count={status.workers.length}
          >
            {adminCopy.system.workersHeading}
          </SectionHeader>
          <Table
            caption={adminCopy.system.workers}
            rows={status.workers}
            getRowId={(w) => w.id}
            columns={[
              {
                key: 'workspace',
                header: adminCopy.system.columns.workspace,
                render: (w) => (
                  <Text variant="code" tone="text-sub">
                    {w.workspace_id}
                  </Text>
                ),
              },
              {
                key: 'address',
                header: adminCopy.system.columns.address,
                render: (w) => (
                  <Text variant="code" tone="text-sub">
                    {w.address}
                  </Text>
                ),
              },
              {
                key: 'pid',
                header: adminCopy.system.columns.pid,
                align: 'end',
                render: (w) =>
                  w.pid != null ? (
                    <Text tone="text-sub" numeric>
                      {w.pid}
                    </Text>
                  ) : (
                    dash
                  ),
              },
              {
                key: 'heartbeat',
                header: adminCopy.system.columns.heartbeat,
                align: 'end',
                render: (w) =>
                  w.heartbeat_age_ms > STALE_HEARTBEAT_MS ? (
                    <Text tone="text-sub" numeric>
                      <StatusDot
                        tone="danger"
                        label={formatAge(w.heartbeat_age_ms)}
                      />
                    </Text>
                  ) : (
                    <Text tone="text-sub" numeric>
                      {formatAge(w.heartbeat_age_ms)}
                    </Text>
                  ),
              },
            ]}
          />
        </section>
      )}
    </Stack>
  )
}
